import React, { useMemo } from "react";
import {
  ArrowUpRight,
  HeartPulse,
  Microscope,
  Syringe,
  type LucideIcon,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import type { TranslationKeys } from "@/i18n/translations";
import { cn } from "@/lib/utils";

type StarterTheme = {
  id: string;
  titleKey: TranslationKeys;
  icon: LucideIcon;
  promptKeys: TranslationKeys[];
};

const STARTER_THEMES: StarterTheme[] = [
  {
    id: "immunization",
    titleKey: "starters.theme.immunization",
    icon: Syringe,
    promptKeys: ["input.suggestion.1", "input.suggestion.2", "input.suggestion.6"],
  },
  {
    id: "maternal",
    titleKey: "starters.theme.maternal",
    icon: HeartPulse,
    promptKeys: ["input.suggestion.3", "input.suggestion.4", "input.suggestion.5"],
  },
  {
    id: "diseases",
    titleKey: "starters.theme.diseases",
    icon: Microscope,
    promptKeys: ["input.suggestion.7", "input.suggestion.8", "input.suggestion.9"],
  },
];

type StarterPromptsProps = {
  onSelect: (prompt: string) => void;
  disabled?: boolean;
  className?: string;
};

export const StarterPrompts: React.FC<StarterPromptsProps> = ({
  onSelect,
  disabled = false,
  className,
}) => {
  const { t } = useLanguage();

  const themes = useMemo(
    () =>
      STARTER_THEMES.map((theme) => ({
        id: theme.id,
        title: t(theme.titleKey),
        Icon: theme.icon,
        prompts: theme.promptKeys.map((key) => t(key)),
      })),
    [t],
  );

  return (
    <div className={cn("w-full", className)}>
      <div className="mb-4 text-center">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-primary-700">
          {t("starters.heading")}
        </h2>
        <p className="mx-auto mt-1.5 max-w-2xl text-balance text-xs leading-relaxed text-slate-500">
          {/* {t("starters.scope")} */}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {themes.map((theme) => (
          <section
            key={theme.id}
            className="rounded-2xl border border-primary-100/50 bg-white/80 p-4 shadow-sm backdrop-blur-sm"
          >
            <h3 className="mb-2.5 flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <theme.Icon className="h-3.5 w-3.5" />
              </span>
              {theme.title}
            </h3>

            <ul className="space-y-1.5">
              {theme.prompts.map((prompt) => (
                <li key={prompt}>
                  <button
                    type="button"
                    onClick={() => onSelect(prompt)}
                    disabled={disabled}
                    className="group flex w-full items-start gap-2 rounded-xl border border-transparent px-2.5 py-2 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
                  >
                    <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-400 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary-600" />
                    <span className="text-[13px] leading-snug text-slate-700 transition-colors group-hover:text-primary-800">
                      {prompt}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};
